import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Award, Globe2, ShieldCheck, Users, BookOpen, ChevronRight } from "lucide-react";
import { usePageMeta, SITE_URL } from "@/hooks/use-page-meta";
import { useJsonLd } from "@/hooks/use-json-ld";
import { buildBreadcrumbList } from "@/lib/breadcrumbs";

const About = () => {
  usePageMeta({
    title: "About ESG Invest — Independent Middle East ESG Ratings | ESG Invest",
    description:
      "ESG Invest is a UN PRI signatory providing independent ESG ratings for 880+ Middle East listed companies. Learn about our team, methodology, and partnerships.",
    path: "/about",
  });

  useJsonLd(
    "ld-about-breadcrumbs",
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
    ]),
  );

  useJsonLd("ld-about-aboutpage", {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": `${SITE_URL}/about#aboutpage`,
    url: `${SITE_URL}/about`,
    name: "About ESG Invest",
    mainEntity: { "@id": `${SITE_URL}/#organization` },
  });

  return (
    <Layout>
      <div className="min-h-screen bg-se-gray-50 dark:bg-background">
        <section className="bg-gradient-to-br from-se-navy via-se-navy to-se-green text-white">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
            <nav className="flex items-center gap-2 text-sm text-white/70 mb-6" aria-label="Breadcrumb">
              <Link href="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white">About</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Independent ESG ratings for Middle East markets
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed">
              ESG Invest is the leading independent provider of Environmental, Social, and
              Governance ratings for listed companies across the GCC and broader Middle East.
              We help institutional investors, asset managers, and family offices integrate ESG
              into their decision-making with sector-adjusted scores grounded in 43 transparent criteria.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <Card className="p-6">
              <div className="w-12 h-12 rounded-lg bg-se-green-lighter dark:bg-se-green/10 flex items-center justify-center mb-4">
                <Globe2 className="w-6 h-6 text-se-green" />
              </div>
              <h3 className="font-semibold text-lg mb-2">880+ companies</h3>
              <p className="text-muted-foreground text-sm">
                Coverage across 10 Middle East markets including Saudi Arabia, the UAE, Qatar, Kuwait, Bahrain, Oman, Jordan, and Lebanon.
              </p>
            </Card>
            <Card className="p-6">
              <div className="w-12 h-12 rounded-lg bg-se-green-lighter dark:bg-se-green/10 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-se-green" />
              </div>
              <h3 className="font-semibold text-lg mb-2">UN PRI signatory</h3>
              <p className="text-muted-foreground text-sm">
                ESG Invest is a signatory of the United Nations Principles for Responsible Investment, formally committed to transparent and responsible analysis.
              </p>
            </Card>
            <Card className="p-6">
              <div className="w-12 h-12 rounded-lg bg-se-green-lighter dark:bg-se-green/10 flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-se-green" />
              </div>
              <h3 className="font-semibold text-lg mb-2">43-criterion methodology</h3>
              <p className="text-muted-foreground text-sm">
                Sector-adjusted scoring across Environmental, Social, and Governance pillars — fully documented and auditable.
              </p>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-3xl font-bold text-se-navy dark:text-foreground mb-4">Our story</h2>
            <p className="text-muted-foreground leading-relaxed">
              Founded in 2017, ESG Invest was created to close the data gap on sustainable investing
              in the Middle East. At a time when global ESG providers offered only thin and
              inconsistent coverage of regional issuers, our team built a methodology grounded in
              local disclosure norms, regulatory frameworks, and sector realities. Today we serve
              asset managers, sovereign wealth funds, family offices, and corporate sustainability
              teams across the GCC and beyond.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Our analysts combine financial training with deep regional expertise. Every rating is
              built bottom-up from public disclosures, regulatory filings, sustainability reports,
              and direct company engagement — never from black-box estimation models.
            </p>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold text-se-navy dark:text-foreground mb-6">What we believe</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-se-green-lighter dark:bg-se-green/10 flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-5 h-5 text-se-green" />
                  </div>
                  <h3 className="font-semibold text-lg pt-1.5">Independence first</h3>
                </div>
                <p className="text-muted-foreground text-sm">
                  We do not consult for the companies we rate. Our revenue comes from investor
                  subscriptions only — no rated-issuer fees, no advisory engagements.
                </p>
              </Card>
              <Card className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-se-green-lighter dark:bg-se-green/10 flex items-center justify-center shrink-0">
                    <BookOpen className="w-5 h-5 text-se-green" />
                  </div>
                  <h3 className="font-semibold text-lg pt-1.5">Transparency by default</h3>
                </div>
                <p className="text-muted-foreground text-sm">
                  Every score is traceable to underlying criteria and source documents. Our
                  full methodology is publicly documented.
                </p>
              </Card>
              <Card className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-se-green-lighter dark:bg-se-green/10 flex items-center justify-center shrink-0">
                    <Globe2 className="w-5 h-5 text-se-green" />
                  </div>
                  <h3 className="font-semibold text-lg pt-1.5">Regional context</h3>
                </div>
                <p className="text-muted-foreground text-sm">
                  Sector and country-level adjustments reflect the disclosure environment and
                  business norms of Middle East issuers — not a reskinned global model.
                </p>
              </Card>
              <Card className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-se-green-lighter dark:bg-se-green/10 flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5 text-se-green" />
                  </div>
                  <h3 className="font-semibold text-lg pt-1.5">Built for investors</h3>
                </div>
                <p className="text-muted-foreground text-sm">
                  Every product decision is filtered through one lens: does it help portfolio
                  managers make better-informed capital allocation decisions?
                </p>
              </Card>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold text-se-navy dark:text-foreground mb-6">Partners &amp; recognition</h2>
            <Card className="p-8">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 text-se-green shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold mb-1">UN PRI signatory</p>
                    <p className="text-sm text-muted-foreground">
                      Committed to the six Principles for Responsible Investment.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 text-se-green shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold mb-1">Stock exchanges partnership</p>
                    <p className="text-sm text-muted-foreground">
                      Methodology partner for ESG-aligned indices and disclosure benchmarking.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 text-se-green shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold mb-1">Industry references</p>
                    <p className="text-sm text-muted-foreground">
                      Cited in UNDP, IFC, and regional regulator publications on Middle East ESG disclosure.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <Card className="p-8 bg-se-navy text-white">
            <div className="md:flex md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Want to see the platform?</h2>
                <p className="text-white/90">
                  Request a trial to access ESG ratings and analytics for 880+ Middle East listed companies.
                </p>
              </div>
              <Button asChild variant="accent" size="lg" className="mt-4 md:mt-0 shrink-0">
                <Link href="/contact">Request trial access</Link>
              </Button>
            </div>
          </Card>
        </section>
      </div>
    </Layout>
  );
};

export default About;
